import { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import mapbackApi from '~/apis/mapback';
import { useAppSelector } from '~/redux/hooks';
import { resolveMapTrackingUsername } from '~/utils/mapTrackingUsername';
import { IMapbackTrackingItem, JourneyData, JourneyPoint, TimelineItemUI } from '~/utils/interface/mapback';
import { formatDate, formatDurationDetailed, formatTimeHM, calculateDurationSeconds } from '../utils/formatters';
import { toCleanJourneyData } from '../utils/toCleanJourneyData';

const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';

function pickFirstString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function formatPlaybackTimeStart(date: Date): string {
  return dayjs(date).startOf('day').format(TIMESTAMP_FORMAT);
}

function formatPlaybackTimeEnd(date: Date): string {
  return dayjs(date).endOf('day').format(TIMESTAMP_FORMAT);
}

function buildTimelineItem(segment: JourneyData['segments'][number], index: number): TimelineItemUI {
  const segmentPoints = Array.isArray(segment.points) ? segment.points : [];
  const firstPoint = segmentPoints[0] as JourneyPoint | undefined;
  const safeStartAddress =
    typeof segment.start_address === 'string' && segment.start_address.trim() ? segment.start_address : '--';

  const displayValue = segment.type === 'route' ? `${segment.total_distance.toFixed(2)} km` : safeStartAddress;

  const durationSecs = calculateDurationSeconds(segment.start_time, segment.end_time);

  return {
    id: `segment-${index}-${segment.start_time}`,
    type: segment.type,
    startTime: formatTimeHM(segment.start_time),
    endTime: formatTimeHM(segment.end_time),
    startDate: formatDate(segment.start_time),
    startAddress: safeStartAddress,
    duration: formatDurationDetailed(durationSecs),
    displayValue,
    points: segmentPoints,
    startCoord: firstPoint ? [firstPoint.latitude, firstPoint.longitude] : [0, 0],
  };
}

export interface UseJourneyDataResult {
  journeyData: JourneyData | null;
  selectedPersonInfo:
    | (Pick<JourneyData, 'person_id' | 'full_name' | 'device_imei'> & {
        latitude?: number | null;
        longitude?: number | null;
      })
    | null;
  timelineItems: TimelineItemUI[];
  allPoints: JourneyPoint[];
  isLoading: boolean;
  error: Error | null;
}

const useJourneyData = (personId: number, timeStart: Date | null, timeEnd: Date | null): UseJourneyDataResult => {
  useTranslation('admin/journey');
  const authUsername = useAppSelector((state) => state.auth.username);
  const userProfile = useAppSelector((state) => state.auth.userProfile);
  const selectedUser = useAppSelector((state) => state.auth.selectedUser);
  const mapTrackingUsername = useMemo(
    () => resolveMapTrackingUsername(userProfile?.username, authUsername, selectedUser?.username),
    [userProfile?.username, authUsername, selectedUser?.username]
  );
  const [journeyData, setJourneyData] = useState<JourneyData | null>(null);
  const [selectedPersonInfo, setSelectedPersonInfo] = useState<
    | (Pick<JourneyData, 'person_id' | 'full_name' | 'device_imei'> & {
        latitude?: number | null;
        longitude?: number | null;
      })
    | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const allPoints = useMemo(() => journeyData?.allPoints ?? [], [journeyData]);

  const timelineItems = (journeyData?.segments ?? []).map((seg, idx) => buildTimelineItem(seg, idx));

  const fetchJourney = useCallback(async () => {
    if (!personId) {
      setJourneyData(null);
      setSelectedPersonInfo(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (!mapTrackingUsername) {
      setIsLoading(true);
      return;
    }

    const trackingUser = mapTrackingUsername;

    if (!timeStart || !timeEnd) {
      try {
        const trackingResponse = await mapbackApi.getMapTracking(trackingUser);
        const trackingList: IMapbackTrackingItem[] = Array.isArray(trackingResponse.data) ? trackingResponse.data : [];
        const matchedTracking = trackingList.find((item) => item.person_id === personId) ?? null;

        setSelectedPersonInfo(
          matchedTracking
            ? {
                person_id: matchedTracking.person_id,
                full_name: pickFirstString(matchedTracking.full_name),
                device_imei: pickFirstString(matchedTracking.device_imei),
                latitude: matchedTracking.latitude,
                longitude: matchedTracking.longitude,
              }
            : null
        );
      } catch {
        setSelectedPersonInfo(null);
      }

      setJourneyData(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [response, trackingResponse] = await Promise.all([
        mapbackApi.getPlayback({
          person_id: personId,
          time_start: formatPlaybackTimeStart(timeStart),
          time_end: formatPlaybackTimeEnd(timeEnd),
        }),
        mapbackApi.getMapTracking(trackingUser),
      ]);

      const trackingList: IMapbackTrackingItem[] = Array.isArray(trackingResponse.data) ? trackingResponse.data : [];
      const matchedTracking = trackingList.find((item) => item.person_id === personId) ?? null;
      setSelectedPersonInfo(
        matchedTracking
          ? {
              person_id: matchedTracking.person_id,
              full_name: pickFirstString(matchedTracking.full_name),
              device_imei: pickFirstString(matchedTracking.device_imei),
              latitude: matchedTracking.latitude,
              longitude: matchedTracking.longitude,
            }
          : null
      );

      const clean = toCleanJourneyData(response.data, { personId, matchedTracking });
      setJourneyData(clean);
    } catch (err) {
      try {
        const trackingResponse = await mapbackApi.getMapTracking(trackingUser);
        const trackingList: IMapbackTrackingItem[] = Array.isArray(trackingResponse.data) ? trackingResponse.data : [];
        const matchedTracking = trackingList.find((item) => item.person_id === personId) ?? null;

        setSelectedPersonInfo(
          matchedTracking
            ? {
                person_id: matchedTracking.person_id,
                full_name: pickFirstString(matchedTracking.full_name),
                device_imei: pickFirstString(matchedTracking.device_imei),
                latitude: matchedTracking.latitude,
                longitude: matchedTracking.longitude,
              }
            : null
        );
      } catch {
        setSelectedPersonInfo(null);
      }

      const axiosErr = err as AxiosError;

      if (axiosErr.response?.status === 404) {
        setJourneyData(null);
        setError(new Error('NOT_FOUND'));
      } else if (!axiosErr.response) {
        setError(new Error('NETWORK'));
      } else {
        setError(new Error('UNKNOWN'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [mapTrackingUsername, personId, timeStart, timeEnd]);

  useEffect(() => {
    fetchJourney();
  }, [fetchJourney]);

  return { journeyData, selectedPersonInfo, timelineItems, allPoints, isLoading, error };
};

export default useJourneyData;
